import React, { useState, useEffect, useRef } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

interface ImageWithLoaderProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  containerSx?: SxProps<Theme>;
  loaderSize?: number;
  loaderColor?: string;
}

const ImageWithLoader: React.FC<ImageWithLoaderProps> = ({
  src,
  alt,
  containerSx,
  loaderSize = 40,
  loaderColor = '#F57C00',
  style,
  onLoad,
  onError,
  ...imgProps
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);

  // Reset loading state when src changes
  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
    
    // Check if image is already loaded (might happen with cached images)
    if (imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth > 0) {
      setIsLoading(false);
    }
  }, [src]);

  // Check if image is loaded on mount/update (handles cached images)
  useEffect(() => {
    const checkImageLoaded = () => {
      if (imgRef.current) {
        if (imgRef.current.complete && imgRef.current.naturalWidth > 0) {
          setIsLoading(false);
        }
      }
    };

    // Small delay to ensure img element is in DOM
    const timer = setTimeout(checkImageLoaded, 0);
    return () => clearTimeout(timer);
  }, [src]);

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setIsLoading(false);
    onLoad?.(e);
  };

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setIsLoading(false);
    setHasError(true);
    onError?.(e);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...containerSx,
      }}
    >
      {isLoading && (
        <CircularProgress
          size={loaderSize}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: loaderColor,
            zIndex: 1,
          }}
        />
      )}
      {!hasError && (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          onLoadStart={() => {
            // Ensure loader is visible when loading starts (even with lazy loading)
            if (!isLoading) {
              setIsLoading(true);
            }
          }}
          style={{
            ...style,
            opacity: isLoading ? 0 : 1,
            transition: 'opacity 0.3s ease-in-out',
          }}
          {...imgProps}
        />
      )}
      {hasError && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#999',
            fontSize: '14px',
            textAlign: 'center',
          }}
        >
          Failed to load image
        </Box>
      )}
    </Box>
  );
};

export default ImageWithLoader;

